import { memo, useCallback, useEffect, useState } from "react";
import { ExpensesSearchTabProps } from "./types";
import { Pagination } from "services/search";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { NoResults } from "ui/components/NoResults";
import { TableSearch } from "ui/components/Table/TableSearch";
import { FINANCE_EXPENSE_SEARCH_COLUMNS } from "ui/modules/finance/pages/expenses/ExpenseSearchPage/components/ExpenseSearchResults/consts";
import { transformRoute, useTranslate } from "helpers";
import { deleteFleetVehicleExpenseAPI, getFleetVehicleExpenseAPI, getFleetVehicleExpensesAPI } from "services/api/fleet/vehicles/expenses/api";
import { FleetVehicleExpenseRow } from "services/api/fleet/vehicles/expenses/types";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import { useNavigate } from "react-router-dom";
import { Dialog } from "ui/components/Dialog";

const ExpensesSearchTab: React.FC<ExpensesSearchTabProps> = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleExpenseRow>
  >(initialDataWithPagination);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [expenseId, setExpenseId] = useState<string>("");
  const [openDialog, setOpenDialog] = useState(false);


  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (pagination: Pagination) => {
    setIsLoading(true);
    try {
      const results = await getFleetVehicleExpensesAPI(pagination);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    var expense = await getFleetVehicleExpenseAPI(id);

    // Opći trošak tvrtke nema vozilo pa ni karticu vozila na koju bi se otišlo.
    if (expense?.vehicle) {
      navigate(
        transformRoute(FleetVehicleRoutes.FleetVehicleExpenseSearchPageRouter, {
          id: expense.vehicle.id,
        })
      );
    }
  }, []);

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setExpenseId(id);
    setOpenDialog(true);

  }

  const handleVehicleClick = useCallback((event: React.MouseEvent, vehicleId: string) => {
    event.stopPropagation();
    event.preventDefault();
    navigate(
      transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, { id: vehicleId })
    );
  }, [navigate]);

  const handleExpenseDeleteDialogConfirm = useCallback(async () => {
    if (!expenseId) return;

    try {
      const deleted = await deleteFleetVehicleExpenseAPI(
        expenseId,
        t
      );

      if (deleted) {
        await fetchSearchResult(searchResults.pagination);
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setExpenseId("");
    } catch (err) {
      console.error("Greška prilikom brisanja vozača:", err);
    }
  }, [expenseId, t, searchResults.pagination]);

  const handleExpenseDeleteDialogClose = useCallback(() => {
    setExpenseId("");
    setOpenDialog(false);
  }, []);

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };



  return (
    <div className="rounded-md mb-4 h-full w-full">
      <div>
        {!isLoading && searchResults.pagination.totalPages === 0 ? (
          <NoResults message="Unesite trošak klikom na 'Dodaj trošak'" />
        ) : (
          <div className="mx-[-1px] mb-[-1px]">
            <TableSearch
              columns={FINANCE_EXPENSE_SEARCH_COLUMNS(t, handleDeleteClick, handleVehicleClick)}
              data={searchResults.data}
              selectedRows={[]}
              pagination={
                searchResults.pagination || initialDataWithPagination.pagination
              }
              selectableRows={false}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>
      <Dialog
        open={openDialog}
        title={t(
          "admin.fleet.vehicle.expense.dialog.delete.title"
        )}
        message={t(
          "admin.fleet.vehicle.expense.dialog.delete.message"
        )}
        confirmText={t(
          "admin.fleet.vehicle.expense.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.fleet.vehicle.expense.dialog.delete.canceltext"
        )}
        onConfirm={handleExpenseDeleteDialogConfirm}
        onCancel={handleExpenseDeleteDialogClose}
      />
    </div>

  );
};

export default memo(ExpensesSearchTab);
